import React from "react";
import {
  Box,
  ListSubheader,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

/**
 * Batch picker (mobile) — the mobile equivalent of the desktop
 * `components/SelectBatch`.
 *
 * Mirrors the desktop behaviour: batches are grouped into "General Assessments"
 * (non-pathway) and "Pathways", the group headers only appear when both groups
 * have entries, and a single enrollment renders as a read-only field instead of
 * a dropdown.
 *
 * The enrollments fetch and the selection itself live in `useSelectedBatch`, so
 * this component is purely presentational.
 *
 * @param {Array}    batches   mapped enrollments
 * @param {object}   value     the selected batch
 * @param {Function} onChange  called with the newly selected batch
 * @param {boolean} [loading]
 */
export default function BatchSelector({
  batches = [],
  value,
  onChange = () => {},
  loading = false,
}) {
  const { generalBatches, pathwayBatches } = React.useMemo(
    () => ({
      generalBatches: batches.filter((batch) => !batch?.forPathway),
      pathwayBatches: batches.filter((batch) => batch?.forPathway),
    }),
    [batches]
  );

  const handleChange = (event) => {
    const batch = batches.find((b) => b._id === event.target.value);
    if (batch) onChange(batch);
  };

  const label = (
    <Typography
      variant="body02-medium"
      color="neutral.clr-600"
      sx={{ mb: 1.5, display: "block" }}
    >
      Batch
    </Typography>
  );

  if (loading) {
    return (
      <Box>
        {label}
        <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!batches.length) return null;

  // A single enrollment has nothing to choose — show it as a plain field, the
  // same call the desktop SelectBatch makes.
  if (batches.length === 1) {
    return (
      <Box>
        {label}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minHeight: 44,
            px: 3,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "neutral.clr-200",
            bgcolor: "shades.clr-white-900",
          }}
        >
          <Typography
            variant="body01-medium"
            color="neutral.clr-900"
            noWrap
            title={batches[0]?.name}
          >
            {batches[0]?.name}
          </Typography>
        </Box>
      </Box>
    );
  }

  const showGroupHeaders = !!generalBatches.length && !!pathwayBatches.length;

  const renderOptions = (group) =>
    group.map((batch) => (
      <MenuItem key={batch._id} value={batch._id}>
        <Typography variant="body01-medium" color="neutral.clr-900" noWrap>
          {batch.name}
        </Typography>
      </MenuItem>
    ));

  return (
    <Box>
      {label}
      <Select
        fullWidth
        size="small"
        value={value?._id || ""}
        onChange={handleChange}
        IconComponent={KeyboardArrowDownIcon}
        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
        sx={{
          borderRadius: 2,
          bgcolor: "shades.clr-white-900",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "neutral.clr-200" },
          "& .MuiSelect-select": { py: 2 },
        }}
      >
        {showGroupHeaders && (
          <ListSubheader sx={{ lineHeight: 2.5, color: "neutral.clr-500" }}>
            General Assessments
          </ListSubheader>
        )}
        {renderOptions(generalBatches)}

        {showGroupHeaders && (
          <ListSubheader sx={{ lineHeight: 2.5, color: "neutral.clr-500" }}>
            Pathways
          </ListSubheader>
        )}
        {renderOptions(pathwayBatches)}
      </Select>
    </Box>
  );
}
